import type { Partner } from 'src/modules/designStudio/helpers/Partner';
import type { ProductDataStore } from 'src/modules/designStudio/helpers/productSelection/ProductDataStore';
import { ProductTypeNames } from 'src/modules/designStudio/helpers/productSelection/constants';
import { PartnerProductsRouterConfig } from 'src/modules/designStudio/helpers/productSelection/contractorFilteringPreferences';
import type {
  Product,
  ProductCategory,
  Variation,
} from 'src/modules/designStudio/helpers/productSelection/types';
import {
  AllProductTypeLabels,
  ProductTypeLabel,
} from 'src/modules/designStudio/helpers/productSelection/types';
import type { Mode } from 'src/modules/designStudio/providers';

export type InventoryStatus = 'REAL' | 'GENERIC' | 'NONE';

export type ProductInventorySummary = Record<ProductTypeLabel, InventoryStatus>;

const createEmptySummary = (): ProductInventorySummary => ({
  [ProductTypeLabel.HORIZONTAL_SIDING]: 'NONE',
  [ProductTypeLabel.VERTICAL_SIDING]: 'NONE',
  [ProductTypeLabel.SHINGLED_SIDING]: 'NONE',
  [ProductTypeLabel.BRICK_SIDING]: 'NONE',
  [ProductTypeLabel.STONE_SIDING]: 'NONE',
  [ProductTypeLabel.CONCRETE_SIDING]: 'NONE',
  [ProductTypeLabel.STUCCO_SIDING]: 'NONE',
  [ProductTypeLabel.SHINGLE_ROOF]: 'NONE',
  [ProductTypeLabel.TILE_ROOF]: 'NONE',
  [ProductTypeLabel.METAL_ROOF]: 'NONE',
  [ProductTypeLabel.TRIM]: 'NONE',
});

const createDebugSummary = (summary: ProductInventorySummary) =>
  JSON.stringify(
    {
      HORIZONTAL_SIDING: summary[ProductTypeLabel.HORIZONTAL_SIDING],
      VERTICAL_SIDING: summary[ProductTypeLabel.VERTICAL_SIDING],
      BRICK_SIDING: summary[ProductTypeLabel.BRICK_SIDING],
      STONE_SIDING: summary[ProductTypeLabel.STONE_SIDING],
      CONCRETE_SIDING: summary[ProductTypeLabel.CONCRETE_SIDING],
      STUCCO_SIDING: summary[ProductTypeLabel.STUCCO_SIDING],
      SHINGLE_ROOF: summary[ProductTypeLabel.SHINGLE_ROOF],
      TILE_ROOF: summary[ProductTypeLabel.TILE_ROOF],
      METAL_ROOF: summary[ProductTypeLabel.METAL_ROOF],
      TRIM: summary[ProductTypeLabel.TRIM],
    },
    undefined,
    2,
  );

export class ProductsRouter {
  isPrintDebugOn: boolean;

  mode: Mode;

  partner: Partner;

  realStore: ProductDataStore;

  genericStore: ProductDataStore;

  constructor(
    realStore: ProductDataStore,
    genericStore: ProductDataStore,
    partner: Partner,
    mode: Mode,
    isPrintDebugOn = false,
  ) {
    this.realStore = realStore;
    this.genericStore = genericStore;
    this.partner = partner;
    this.mode = mode;
    this.isPrintDebugOn = isPrintDebugOn;

    if (isPrintDebugOn) {
      // eslint-disable-next-line no-console
      console.info(
        `ProductsRouter created with:${JSON.stringify(
          { mode, partner },
          undefined,
          2,
        )}`,
      );
    }
  }

  getProductTypeFromTextureId(
    category: ProductCategory,
    textureID: number,
  ): ProductTypeLabel {
    return (
      this.realStore.getProductTypeFromTextureId(category, textureID) ||
      this.genericStore.getProductTypeFromTextureId(category, textureID)
    );
  }

  private getProductsWithStatus(productType: ProductTypeLabel): {
    status: InventoryStatus;
    products: Product[];
  } {
    const partnerRouterConfig = PartnerProductsRouterConfig[this.partner];

    const realProducts = this.realStore.getProducts(productType);
    const genericProducts = this.genericStore.getProducts(productType);

    const productTypePreference =
      typeof partnerRouterConfig === 'string'
        ? partnerRouterConfig
        : partnerRouterConfig[productType];

    switch (productTypePreference) {
      case 'REAL':
        return {
          status: realProducts.length === 0 ? 'NONE' : 'REAL',
          products: realProducts,
        };
      case 'REAL_WITH_GENERIC_FALLBACK':
        if (realProducts.length !== 0) {
          return {
            status: 'REAL',
            products: realProducts,
          };
        }
        if (genericProducts.length !== 0) {
          return {
            status: 'GENERIC',
            products: genericProducts,
          };
        }
        return {
          status: 'NONE',
          products: [],
        };
      case 'GENERIC':
        return {
          status: genericProducts.length === 0 ? 'NONE' : 'GENERIC',
          products: genericProducts,
        };
      case 'NONE':
        return {
          status: 'NONE',
          products: [],
        };
      default:
        return {
          status: 'NONE',
          products: [],
        };
    }
  }

  getProducts(productType: ProductTypeLabel): Product[] {
    const result = this.getProductsWithStatus(productType);
    if (this.isPrintDebugOn) {
      // eslint-disable-next-line no-console
      console.info(
        `ProductsRouter.getProducts(${
          ProductTypeNames[productType]
        }) => ${JSON.stringify(
          { status: result.status, numProducts: result.products.length },
          undefined,
          2,
        )}`,
      );
    }
    return result.products;
  }

  getProviderNameByUUID(uuid: string): string | undefined {
    return (
      this.realStore.getProviderNameByUUID(uuid) ||
      this.genericStore.getProviderNameByUUID(uuid)
    );
  }

  getVariationByUUID(
    uuid: string,
    category: ProductCategory,
  ): Variation | undefined {
    return (
      this.realStore.getVariationByUUID(uuid, category) ||
      this.genericStore.getVariationByUUID(uuid, category)
    );
  }

  getInventorySummary(): ProductInventorySummary {
    const summary = createEmptySummary();

    AllProductTypeLabels.forEach(productType => {
      summary[productType] = this.getProductsWithStatus(productType).status;
    });

    if (this.isPrintDebugOn) {
      // eslint-disable-next-line no-console
      console.info(
        `ProductsRouter.getInventorySummary()\n${createDebugSummary(summary)}`,
      );
    }

    return summary;
  }

  removeProvidersNotInListFromRealStore(providerIds: string[]) {
    if (this.isPrintDebugOn) {
      // eslint-disable-next-line no-console
      console.info(
        `In ProductsRouter.removeProvidersNotInListFromRealStore, removing providers not in list:\n${JSON.stringify(
          providerIds,
          undefined,
          2,
        )}`,
      );
    }
    this.realStore.removeProvidersNotInList(providerIds, this.isPrintDebugOn);
  }
}
